/**
 * The planning board's own cell key: which pay period, which employee, and
 * which date column. Shared by SupervisorPlanningBoardPage.tsx, which
 * builds and reads it, and planningBoardDraftStore.ts, which uses it to purge
 * a transferred employee's own stale entries from the team they left — both
 * sides need to agree on the exact same format. Keyed by employee id (a display name is not unique),
 * not row index: a transfer can change which row an employee falls on
 * within a team's own roster, so a row-indexed key would silently point
 * at whoever a later reshuffle happened to land on that row instead. '|'
 * rather than '-', since the key is split on it.
 */
export type Period = 'current' | 'next'

export function cellKey(period: Period, employeeId: string, col: number): string {
  return `${period}|${employeeId}|${col}`
}

// The reverse of cellKey. Splits from both ends rather than assuming
// exactly three '|'-separated parts, so a name that happens to contain '|'
// still parses (an id never does, but the key format
// should not silently corrupt one that did). Returns an empty
// employeeId for a key in the pre-'|' row-indexed format rather than
// throwing: keepCurrentKeyFormat below is what drops that shape, and a
// caller that skips it and compares this empty string against a real name
// simply never matches, rather than crashing.
export function parseCellKey(key: string): { period: Period; employeeId: string; col: number } {
  const parts = key.split('|')
  const period = parts[0] as Period
  const col = Number(parts[parts.length - 1])
  const employeeId = parts.slice(1, -1).join('|')
  return { period, employeeId, col }
}

// A draft, history entry, or publication saved by an older build keyed a cell by
// row index (period-row-col, no '|'), a format nothing reads any more: a row
// index says nothing about which employee a row belonged to once the roster
// comes from the database. Dropped rather than left to mismatch every
// current-format key it is later compared against.
export function keepCurrentKeyFormat<T extends { key: string }>(entries: T[]): T[] {
  return entries.filter((entry) => entry.key.includes('|'))
}
